import { useCallback, useEffect, useState } from 'react';
import { FormattedLocation } from '@zonofi/zono-money-store';
import { getConfigValue } from '@zonofi/common/functions/get-config-value/get-config-value';
import { useDebounceSearch } from './use-debounce-search';

const GOOGLE_MAP_API_KEY = getConfigValue('GOOGLE_MAP_API_KEY');
const extractAddressFields = (result: any) => {
  const components = result.address_components;

  const find = (type: string) => {
    const c = components.find((x: any) => x.types.includes(type));
    return c ? c.long_name : '';
  };

  const premise = find('premise');
  const sublocality =
    find('sublocality') ||
    find('sublocality_level_2') ||
    find('sublocality_level_1');

  return {
    address: [premise, sublocality].filter(Boolean).join(', ') || '',
    city: find('locality') || find('administrative_area_level_2') || '',
    state: find('administrative_area_level_1') || '',
    pincode: find('postal_code') || '',
  };
};

interface UseCurrentLocationProps {
  shouldFetch?: boolean;
  isOnline?: boolean;
}

export const useCurrentLocation = ({
  shouldFetch = true,
  isOnline = false,
}: UseCurrentLocationProps) => {
  const [loading, setLoading] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<FormattedLocation>();
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const { debouncedSearchValue } = useDebounceSearch();

  const reverseGeocode = useCallback(
    async (lat?: number, lng?: number) => {
      try {
        setLoading(true);

        let url = '';

        if (debouncedSearchValue?.trim()) {
          // Address-based geocoding
          url = `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(debouncedSearchValue.trim())}&key=${GOOGLE_MAP_API_KEY}`;
        } else if (lat !== undefined && lng !== undefined) {
          // Reverse geocoding
          url = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&location_type=ROOFTOP&result_type=route|premise|street_address&key=${GOOGLE_MAP_API_KEY}`;
        } else {
          setLoading(false);
          return;
        }

        const res = await fetch(url);
        const data = await res.json();

        if (
          data?.status === 'OK' &&
          Array.isArray(data.results) &&
          data.results.length > 0
        ) {
          const item = data.results[0];
          const completeAddress = extractAddressFields(item);

          const newLat = item.geometry.location.lat;
          const newLng = item.geometry.location.lng;

          // Move map if exists
          mapInstance?.panTo({
            lat: newLat,
            lng: newLng,
          });

          setCurrentLocation({
            lat: String(newLat),
            lng: String(newLng),
            formatted_address: item.formatted_address || '',
            ...completeAddress,
          });
        } else {
          console.log('Geocode failed:', data?.status);
        }
      } catch (err) {
        console.log('Geocode error:', err);
      } finally {
        setLoading(false);
      }
    },
    [debouncedSearchValue, mapInstance]
  );

  const fetchLocation = useCallback(() => {
    // If searchKey exists → skip geolocation
    if (debouncedSearchValue?.trim()) {
      reverseGeocode();
      return;
    }

    setLoading(true);

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        if (isOnline) {
          reverseGeocode(latitude, longitude);
        } else {
          setCurrentLocation({
            lat: String(latitude),
            lng: String(longitude),
          });
          setLoading(false);
        }
      },
      (err) => {
        setLoading(false);
        console.log('Location Error: ', err);
      },
      {
        enableHighAccuracy: false,
        timeout: 15000,
      }
    );
  }, [debouncedSearchValue, isOnline]);

  useEffect(() => {
    if (!shouldFetch) return;
    fetchLocation();
  }, [debouncedSearchValue, shouldFetch]);

  return {
    currentLocation,
    fetchLocation,
    loading,
    setMapInstance,
    mapInstance,
    reverseGeocode,
  };
};
